import { AssetGroup } from '@play-co/timestep-core';
import {
  animate,
  ImageView,
  ImageViewOpts,
  MovieClip,
  View,
} from '@play-co/timestep-core/ui';
import TierView from 'src/game/catalog/TierView';
import TutorialHand from 'src/game/components/shared/TutorialHand';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import LazyImageCoverView from 'src/lib/ui/components/LazyImageCoverView';
import { animDuration, waitForAssets, waitForItPromise } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { GachaItem } from 'src/replicant/gacha/gacha.ruleset';
import { assertNever } from 'src/replicant/utils';
import StateObserver from 'src/StateObserver';
import PopupBasicLazy from '../../components/popups/PopupBasicLazy';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { capitalize } from 'src/replicant/utils/string';
import { getGachaRewardIcon } from '../gacha.util';

export const balls: Record<number, ImageViewOpts> = {
  1: {
    centerOnOrigin: true,
    width: 249,
    height: 255,
    image: `assets/ui/gacha/balls/c.png`,
  },
  2: {
    centerOnOrigin: true,
    width: 249,
    height: 255,
    image: `assets/ui/gacha/balls/r.png`,
  },
  3: {
    centerOnOrigin: true,
    width: 249,
    height: 255,
    image: `assets/ui/gacha/balls/sr.png`,
  },
  4: {
    centerOnOrigin: true,
    width: 249,
    height: 255,
    image: `assets/ui/gacha/balls/ur.png`,
  },
  5: {
    centerOnOrigin: true,
    width: 258,
    height: 260,
    image: `assets/ui/gacha/balls/ssr.png`,
  },
};

export type GachaBaseSkin = 'bronze' | 'silver' | 'gold' | 'regular';

export class PopupGacha extends PopupBasicLazy {
  private radial!: MovieClip;
  private sparkles!: MovieClip;
  private gachasClip!: MovieClip;
  private hand: TutorialHand | undefined;
  private clickMessage: LangBitmapFontTextView;
  private descriptionLayout: View;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: 100,
      height: 525,
      darkerBg: false,
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.POPUP._,
      },
    });

    this.box.removeFromSuperview();

    const { width, height } = this.getView().style;
    const x = width / 2;
    const y = height / 2 + 400;

    this.descriptionLayout = new FlexBoxView({
      superview: this.getView(),
      x: (width - 502) * 0.5,
      y: 214,
      width: 502,
      height: 200,
      visible: false,
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
    });

    this.clickMessage = new LangBitmapFontTextView({
      superview: this.getView(),
      x,
      y,
      width: 300,
      height: 40,
      color: '#ffffff',
      localeText: () => i18n('gacha.tapToContinue'),
      font: 'Body',
      size: 30,
      visible: false,
      zIndex: 100,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
    });

    this.hand = new TutorialHand({
      superview: this.getView(),
      visible: false,
      zIndex: 100,
    });
  }

  public init({
    rewards,
    baseSkin = 'regular',
  }: {
    rewards: GachaItem[];
    baseSkin?: GachaBaseSkin;
  }): void {
    super.init({});

    this.spinGacha({ rewards, baseSkin });
  }

  /**
   * Update item information
   */
  private updateDescription(item: GachaItem) {
    this.descriptionLayout.removeAllSubviews();

    const lang = StateObserver.getLocale();
    switch (lang) {
      case 'en':
        this.createItemName(this.descriptionLayout, () =>
          capitalize(item.name_en),
        );
        break;
      case 'ja':
        this.createItemName(this.descriptionLayout, () => item.name_jp);
        break;
      default:
        assertNever(lang);
    }

    const type = item.type;

    switch (type) {
      case 'decoration':
        this.createItemRoom(this.descriptionLayout, () =>
          i18n(`decoration.rooms.${item.room}`),
        );
        this.createItemTheme(this.descriptionLayout, () =>
          i18n(`decoration.themes.${item.theme}`),
        );

        break;
      case 'accessory':
        this.createItemTheme(this.descriptionLayout, () =>
          i18n(`accessory.themes.${item.theme}`),
        );
        break;

      default:
        assertNever(type);
    }
  }

  protected getAssetsGroup(): AssetGroup | null {
    return new AssetGroup([
      {
        url: 'assets/ui/gacha/animation',
        loadMethod: MovieClip.animationLoader,
      },
      ...AssetGroup.constructURLs([
        `assets/sounds/sfx/TurnHandle.mp3`,
        `assets/sounds/sfx/GachaItemDrop.mp3`,
        `assets/sounds/sfx/OpenCapsule.mp3`,
      ]),
    ]);
  }

  private async spinGacha({
    rewards,
    baseSkin,
  }: {
    rewards: GachaItem[];
    baseSkin: GachaBaseSkin;
  }) {
    const { width, height } = this.getView().style;
    const x = width / 2;
    const y = height / 2;

    // Start loading assets before the animation starts.
    void new AssetGroup(rewards.map(getGachaRewardIcon)).load();

    const baseSkinPath = `assets/ui/gacha/bases/gachabase_${baseSkin}.png`;
    await waitForAssets(new AssetGroup([baseSkinPath]), {
      feature: Feature.GACHA._,
      subFeature: Feature.GACHA.SKIN_ASSETS,
    });

    const musicMuted = sounds.musicMuted;
    sounds.musicMuted = true;

    const machineClip = new MovieClip({
      superview: this.getView(),
      x,
      y,
      url: 'assets/ui/gacha/animation',
      centerOnOrigin: true,
    });

    machineClip.addViewSubstitution(
      'p_gachamachinebase',
      new ImageView({
        centerOnOrigin: true,
        width: 682,
        height: 582,
        image: baseSkinPath,
      }),
    );

    this.gachasClip = new MovieClip({
      x,
      y,
      superview: this.getView(),
      url: 'assets/ui/gacha/animation',
      centerOnOrigin: true,
      visible: false,
    });

    this.radial = new MovieClip({
      superview: this.getView(),
      x,
      y,
      url: 'assets/ui/gacha/animation',
      centerOnOrigin: true,
      visible: false,
    });

    this.sparkles = new MovieClip({
      superview: this.getView(),
      x,
      y,
      url: 'assets/ui/gacha/animation',
      defaultAnimation: 'sparkles',
      centerOnOrigin: true,
      visible: false,
    });

    await machineClip.playAsync('machine_appear');
    sounds.playSound('gachaTurnHandle');
    await machineClip.playAsync('machine_turnhandle');
    this.gachasClip.show();

    // Subsitute gacha balls with the actual reward rarity.
    rewards.forEach((reward, i) => {
      this.gachasClip.addViewSubstitution(
        `p_gachabase_${i + 1}`,
        new ImageView(balls[reward.tier]),
      );
    });

    const wait1500 = waitForItPromise(1500);

    await Promise.all([
      this.gachasClip.playAsync(
        `gacha_emerge${rewards.length > 1 ? '_multiple' : '_single'}`,
      ),
      machineClip.playAsync('machine_exit'),
    ]);

    await wait1500;
    machineClip.removeFromSuperview();

    if (rewards.length > 1) {
      await this.multipleRewards(rewards);
    } else {
      await this.singleReward(rewards[0]);
    }

    await Promise.all([
      animate(this.sparkles)
        .then({ opacity: 0 }, animDuration * 2)
        .toPromise(),
      animate(this.radial)
        .then({ opacity: 0 }, animDuration * 2)
        .toPromise(),
    ]);

    sounds.musicMuted = musicMuted;

    this.opts.close();
  }

  private async singleReward(reward: GachaItem) {
    this.radial.show();

    sounds.playSound('gachaOpenCapsule');
    const wait250 = waitForItPromise(250);

    await this.radial.playAsync('radial_appear_start');
    await wait250;

    this.gachasClip.removeFromSuperview();
    const fadeOut = await this.animateReward(reward);

    await this.radial.playAsync('radial_appear_end');
    this.radial.loop('radial_loop');

    await this.waitForClick();
    void fadeOut();
  }

  private async multipleRewards(rewards: GachaItem[]) {
    this.radial.show();
    await this.radial.playAsync('radial_appear_start');
    this.gachasClip.removeFromSuperview();
    await this.radial.playAsync('radial_appear_end');
    this.radial.loop('radial_loop');

    for (let i = 0; i < rewards.length; i++) {
      const reward = rewards[i];
      this.gachasClip.addViewSubstitution(
        `p_gachabase_1`,
        new ImageView(balls[reward.tier]),
      );
      this.getView().addSubview(this.gachasClip);
      await Promise.all([
        this.gachasClip.playAsync('gacha_emerge_oneatatime'),
        waitForItPromise(500).then(() => {
          sounds.playSound('gachaOpenCapsule');
        }),
      ]);

      const [fadeOut] = await Promise.all([
        this.animateReward(reward),
        this.gachasClip.playAsync('gachaball1_fadeout'),
      ]);

      await this.waitForClick();

      if (i === rewards.length - 1) {
        void fadeOut();
      } else {
        await fadeOut();
      }
    }
  }

  public async animateReward(reward: GachaItem) {
    const image = getGachaRewardIcon(reward);

    const itemImage = new LazyImageCoverView({
      superview: this.getView(),
      image,
      mode: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      width: 350,
      height: 350,
      scale: 0.5,
      opacity: 0,
      centerOnOrigin: true,
      x: this.getView().style.width / 2,
      y: this.getView().style.height / 2,
      spinnerColor: '#d195b8',
    });

    const tierView = new TierView({
      superview: itemImage,
      centerOnOrigin: true,
      x: itemImage.style.width / 2,
      y: itemImage.style.height + 28,
      width: itemImage.style.width,
      height: 46,
      star: {
        width: 45,
        height: 46,
      },
    });

    tierView.setTier(reward.tier, reward.tier);

    this.sparkles.updateOpts({
      visible: reward.tier > 1,
    });

    this.updateDescription(reward);
    this.descriptionLayout.style.opacity = 0;

    sounds.playSound('gachaItemDrop');
    await animate(itemImage.style)
      .then({ scale: 1, opacity: 1 }, 500)
      .toPromise();

    this.descriptionLayout.show();
    animate(this.descriptionLayout).then({ opacity: 1 }, animDuration * 2);

    return () =>
      Promise.all([
        animate(this.descriptionLayout)
          .then({ opacity: 0 }, animDuration * 2)
          .then(() => {
            this.descriptionLayout.hide();
          })
          .toPromise(),
        animate(itemImage)
          .then({ opacity: 0 }, 500)
          .then(() => {
            itemImage.removeFromSuperview();
          })
          .toPromise(),
      ]);
  }

  private waitForClick() {
    this.clickMessage.show();
    this.hand?.fadeIn(
      this.clickMessage.style.x,
      this.clickMessage.style.y,
      false,
    );

    return new Promise<void>((resolve) => {
      this.getView().once('InputSelect', () => {
        sounds.playSound('ok');
        this.clickMessage.hide();
        this.hand?.fadeOut();
        resolve();
      });
    });
  }

  /**
   * Create item name
   */
  private createItemName(superview: View, localeText: () => string) {
    const name = new LangBitmapFontTextView({
      superview,
      x: 0,
      y: 0,
      size: 48,
      width: superview.style.width,
      height: 60,
      align: 'center',
      verticalAlign: 'center',
      font: bitmapFonts('Body'),
      localeText,
      letterSpacing: -2,
      color: '#FFFFFF',
    });

    getFlexNode(name).updateStyle({
      marginBottom: 10,
    });

    return name;
  }

  /**
   * Create room name label
   */
  private createItemRoom(superview: View, localeText: () => string) {
    const view = new View({
      superview,
      width: superview.style.width,
      height: 40,
    });

    const name = new LangBitmapFontTextView({
      superview: view,
      x: 45,
      y: 0,
      size: 36,
      width: 222,
      height: 40,
      align: 'left',
      verticalAlign: 'center',
      font: bitmapFonts('Body'),
      localeText,
      letterSpacing: -2,
      color: '#FFFFFF',
    });

    new ImageView({
      superview: view,
      image: 'assets/ui/catalog/icons/itemdetail_room_white.png',
      x: 0,
      y: 0,
      width: 40,
      height: 40,
    });

    return name;
  }

  /**
   * Create room name label
   */
  private createItemTheme(superview: View, localeText: () => string) {
    const view = new View({
      superview,
      width: superview.style.width,
      height: 40,
    });

    const name = new LangBitmapFontTextView({
      superview: view,
      x: 45,
      y: 0,
      size: 36,
      width: 222,
      height: 40,
      align: 'left',
      verticalAlign: 'center',
      font: bitmapFonts('Body'),
      localeText,
      letterSpacing: -2,
      color: '#FFFFFF',
    });

    new ImageView({
      superview: view,
      image: 'assets/ui/catalog/icons/itemdetail_theme_white.png',
      x: 0,
      y: 0,
      width: 40,
      height: 40,
    });

    return name;
  }
}
